/**
 * DYNAMIC TESTIMONIALS
 * Fetches testimonials from shared backend
 * Updates automatically when database is updated
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';
import { getTestimonials } from '../services/api';

// Fallback testimonials if API fails
const fallbackTestimonials = [
  {
    id: '1',
    name: 'Raghu Ram',
    rating: 5,
    text: 'Very experienced and highly qualified doctor. Explained the root cause clearly and gave confidence during surgery. Grateful to Dr. Reddy and team.',
    procedure: 'Shoulder Surgery',
    verified: true
  },
  {
    id: '2',
    name: 'Rama Krishna U',
    rating: 5,
    text: 'Underwent treatment. Dr. Reddy is patient and supportive; had an excellent recovery and regained full movement.',
    procedure: 'Expert Treatment',
    verified: true
  },
  {
    id: '3',
    name: 'Vikas Soni',
    rating: 5,
    text: 'Underwent arthroscopic treatment. Excellent recovery and professional guidance.',
    procedure: 'Arthroscopic Surgery',
    verified: true
  },
  {
    id: '4',
    name: 'Kunal Mahendru',
    rating: 5,
    text: 'All doctors suggested surgery—but Dr. Reddy identified the real issue and guided me to recovery through physiotherapy. A true professional.',
    procedure: 'Conservative Treatment',
    verified: true
  },
  {
    id: '5',
    name: 'Prasad SVV',
    rating: 5,
    text: 'Doctor analyzed scans in detail and guided recovery perfectly. Very knowledgeable and skilled surgeon.',
    procedure: 'Surgical Treatment',
    verified: true
  },
  {
    id: '6',
    name: 'Sarika Otle',
    rating: 5,
    text: 'Absolutely top and compassionate doctor. Humane, skilled, and genuinely cares for patients. One of the best.',
    procedure: 'General Care',
    verified: true
  }
];

export const Testimonials = () => {
  const { theme, doctor, id: siteId } = useSite();
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getTestimonials(siteId);
        if (data && data.length > 0) {
          setTestimonials(data);
        }
      } catch (error) {
        console.log('Using fallback testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [siteId]);

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Patient Success Stories
          </h2>
          <p className="text-lg text-gray-600">
            Real experiences from patients treated by {doctor.name}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-sm text-gray-600 font-medium">5.0 out of 5 (100+ reviews)</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((testimonial, index) => (
            <Card 
              key={testimonial.id || index} 
              className="relative hover:shadow-lg transition-shadow"
              data-testid={`testimonial-${index}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  {testimonial.verified && (
                    <span 
                      className="text-xs px-2 py-1 rounded text-white"
                      style={{ backgroundColor: theme.primaryHex }}
                    >
                      Verified
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">{testimonial.procedure}</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">"{testimonial.text}"</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(doctor.name)}+Reviews`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center font-medium hover:opacity-80"
            style={{ color: theme.primaryHex }}
          >
            Read More Reviews on Google
            <Star className="w-4 h-4 ml-2" />
          </a>
        </div>
      </div>
    </section>
  );
};
